import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { debounce } from 'lodash';
import classnames from 'classnames';
import { push, replace } from 'connected-react-router';
import { isRejected } from '@reduxjs/toolkit';
import { ComboBox, Option, OptionGroup } from '@saucelabs/ds-inputs';
import { IconAndroidFill, IconIosFill } from '@saucelabs/ds-icons';
import { Tooltip } from '@saucelabs/ds-tooltip';
import { Badge } from '@saucelabs/ds-badge';
import { Typography } from '@saucelabs/ds-typography';
import {
  constants as appStorageConstants,
  records as appStorageRecords,
} from '@saucelabs/sl-module-app-storage';
import { actions as notificationsActions } from '@saucelabs/sl-container-notifications';
import { Link } from '@saucelabs/ds-link';

import { PathParamsType } from './MobileAppSelection';
import { constants, utils } from '../';
import {
  actions as testConfigActions,
  selectors as testConfigSelectors,
} from '../../testConfiguration';
import actions from '../actions';
import selectors from '../selectors';
import ThumbnailAndroid from '../../../images/thumbnail-android.svg';
import ThumbnailIOS from '../../../images/thumbnail-iOS.svg';
import Styles from './AppSearchAndSelect.styles';
import EmptyState from './EmptyState';
import { SelectedStateIcons, SelectedStateVersions } from './SelectedState';
import {
  actions as realDeviceSelectionActions,
  constants as realDeviceSelectionConstants,
  utils as realDeviceSelectionUtils,
} from '../../realDeviceSelection';
import FileIcon from './FileIcon';
import { GlobalState } from '../../../../../web-ui-app-management/src/reducers';

const { APP_STORAGE_KIND } = appStorageConstants;
const {
  MOBILE_SELECTION_FILTER,
  MOBILE_SELECTION_FILTER_CATEGORY,
} = realDeviceSelectionConstants;

const DefaultAppIcon = Object.freeze({
  [APP_STORAGE_KIND.IOS]: <ThumbnailIOS className="h-5 w-5 mr-2" />,
  [APP_STORAGE_KIND.ANDROID]: <ThumbnailAndroid className="h-5 w-5 mr-2" />,
});

interface SectionLayout {
  id: string,
  label: string,
  files: appStorageRecords.AppStorageFile[],
}

const LinkToAppStorage = () => (
  <div className="py-0.5 bg-gray-50 flex items-center justify-center">
    <Typography
      component="span"
      variant="body-small"
    >
      <span>Could not find what you need? </span>
      <Link
        inline
        to="/app-management"
      >
        Go to App Management
      </Link>
    </Typography>
  </div>
);

// Customize the virtualized list layout options, so they have appropriate heights for the custom
// elements / layout.
const layoutOptions = {
  estimatedRowHeight: 70,
  placeholderHeight: 70,
  estimatedHeadingHeight: 70,
};

const FileOption = ({ file }: {file: appStorageRecords.AppStorageFile}) => {
  const appName = file.getDisplayName();
  const appInfoString = utils.buildAppInfoString(file);
  return (
    <>
      <span className="flex items-center">
        {file.getIconUrl() ? (
          <FileIcon
            alt="App icon."
            className="h-5 w-5 mr-2"
            file={file}
          />
        ) : DefaultAppIcon[file.kind]}
        <span className={Styles['text-content']}>
          <Typography
            className="max-w-full inline-flex items-center truncate font-semibold"
            component="span"
            variant="body"
          >
            {file.kind === APP_STORAGE_KIND.ANDROID ?
              <IconAndroidFill className="inline-block h-2 w-2 mr-1 text-disabled-emphasis fill-current" /> :
              <IconIosFill className="inline-block h-2 w-2 mr-1 text-disabled-emphasis fill-current" />}
            {appName}
            {file.kind === APP_STORAGE_KIND.IOS && (
              <Badge
                className="ml-1"
                color={file.isSimulatorBuild() ? 'green' : 'blue'}
                emphasis="low"
              >
                {file.isSimulatorBuild() ? 'Simulator' : 'Real Device'}
              </Badge>
            )}
          </Typography>
          <br />
          <Tooltip
            placement="top"
            title={appInfoString}
          >
            <Typography
              className="max-w-full inline-block items-center truncate text-medium-emphasis"
              variant="body"
            >
              {appInfoString}
            </Typography>
          </Tooltip>
          <br />
        </span>
      </span>
    </>
  );
};

const AppSearchAndSelect: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const location = useLocation();
  const { fileId, tab } = useParams<PathParamsType>();
  const [searchTerm, setSearchTerm] = useState('');
  // this is needed for the workaround to not start a backend search
  // when we set the current selected app based on the file id from the param
  // since there is a race condition where setting the id as the selected key of the combo box
  // triggers a search with the app name where the results potentially
  // do not contain the requested app id (if there are more than 100 files found)
  // another case when the user selects a file from the dropdown we don't want to execute the search
  const shouldTriggerSearchRoutine = useRef(true);
  const {
    files,
    isLoadingFiles,
    moreFilesAvailable,
  } = useSelector(selectors.mobileAppSelectionSelector);
  const mobileAppConfiguration = useSelector<GlobalState, appStorageRecords.AppStorageFile | null>(
    testConfigSelectors.mobileAppConfigurationSelector,
  );
  const { realDeviceConfiguration } = useSelector(testConfigSelectors.testConfigurationSelector);

  // needed since we're fetching files debounced
  // and don't want to have the <ComboBox /> loading state flash
  const [showLoadingState, setShowLoadingState] = useState(isLoadingFiles);
  useEffect(() => setShowLoadingState(isLoadingFiles), [isLoadingFiles]);

  const adaptToFileSelection = useCallback((
    file: appStorageRecords.AppStorageFile | null,
    { replaceUrl }: { replaceUrl?: boolean } = { replaceUrl: false },
  ) => {
    const adaptUrl = replaceUrl ? replace : push;
    const formFactors = file?.getDeviceFamily();
    const nextUrl = utils.getUrlForMobileSelection(file, tab);
    const currentUrl = `${location.pathname}${location.search}`;
    const isAppCompatibleWithSelectedDevice = !file || realDeviceSelectionUtils
      .isAppStorageFileCompatibleWithRealDeviceDescriptor(file, realDeviceConfiguration);

    // workaround to remove focus from <ComboBox /> when selection changes
    const element = document.activeElement;
    if (element instanceof HTMLInputElement) {
      setTimeout(() => element.blur(), 10);
    }

    dispatch(testConfigActions.updateTestConfigValue({
      // if the app isn't found by id, just unset the app
      mobileAppConfiguration: file ?? null,
      realDeviceConfiguration: isAppCompatibleWithSelectedDevice ? realDeviceConfiguration : null,
    }));

    dispatch(realDeviceSelectionActions.setFilters([
      {
        category: MOBILE_SELECTION_FILTER_CATEGORY.OS,
        values: file?.kind ? [file.kind.toUpperCase()] : [],
      },
      {
        category: MOBILE_SELECTION_FILTER_CATEGORY.OS_VERSION,
        values: [],
      },
      {
        category: MOBILE_SELECTION_FILTER_CATEGORY.MANUFACTURER,
        values: [],
      },
      {
        category: MOBILE_SELECTION_FILTER_CATEGORY.FORM_FACTOR,
        values: (formFactors?.length === 1) ? [formFactors[0].toUpperCase()] : [],
      },
      {
        category: MOBILE_SELECTION_FILTER_CATEGORY.COMPATIBILITY,
        values: [MOBILE_SELECTION_FILTER.COMPATIBILITY],
      },
    ]));

    if (nextUrl !== currentUrl) {
      dispatch(adaptUrl(utils.getUrlForMobileSelection(file, tab)));
    }
  }, [dispatch, tab, location, realDeviceConfiguration]);

  const fetchAppsDebounced = useMemo(() => (
    debounce((searchQuery) => {
      void dispatch(actions.fetchAppStorageFiles({
        searchQuery,
        shouldCancelPreviousRequest: true,
      }));
    }, constants.FETCH_FILES_DEBOUNCE_THRESHOLD_MS)
  ), [dispatch]);

  const handleSearch = useCallback((searchQuery = '') => {
    if (shouldTriggerSearchRoutine.current) {
      if (!searchQuery) {
        adaptToFileSelection(null);
      }
      setSearchTerm(searchQuery);
      setShowLoadingState(true);
      fetchAppsDebounced(searchQuery);
    }
    // this might look weird but what we want to achieve is
    // not to execute the first time this is called and the flag is still false
    shouldTriggerSearchRoutine.current = true;
  }, [fetchAppsDebounced, adaptToFileSelection]);

  useEffect(() => {
    void dispatch(actions.fetchAppStorageFiles({ fileId })).then((response) => {
      if (fileId && !isRejected(response)) {
        const { payload: { items } } = response;
        // requested file was not found
        if ((items.length === 0)) {
          void dispatch(notificationsActions.notifyWarning(
            'The application build was not found.', null, true, undefined, true,
          ));
        // set the found file as selected
        } else if (items.length === 1) {
          shouldTriggerSearchRoutine.current = false; // set flag to false to prevent the next call to handleSearch being executed
          adaptToFileSelection(items[0], { replaceUrl: true });
        }
      }
    });
    return () => {
      dispatch(testConfigActions.updateTestConfigValue({
        // unset the app when "unmounting"
        mobileAppConfiguration: null,
      }));
    };
  // ignoring deps is by purpose because this logic only applies to the initial rendering
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This section is only required to build our dynamic 'section' component. Nix it if design is
  // good with it so, we can use just the files from the store instead :)
  const hasSearchTerm = !!searchTerm;
  const sectionLayout: SectionLayout[] = useMemo(() => (files.length ? [
    {
      id: hasSearchTerm ? 'search-results' : 'latest-builds',
      label: hasSearchTerm ? 'App builds found' : 'Latest app builds',
      files,
    },
  ] : []), [files, hasSearchTerm]);

  return (
    <div
      className="w-full max-w-xl"
      data-qa="app-search-and-select"
    >
      <ComboBox
        allowsEmptyCollection
        aria-label="Select an app to test"
        className={classnames({
          [Styles['no-list-items']]: !isLoadingFiles && !searchTerm && files.length === 0,
        })}
        data-qa="app-search-and-select-combo-box"
        // The filter, by default, is checking that text exists inside the textValue. Since we're
        // using asynchronous results, we're doing that filtering on the server. Return all
        // entries here.
        defaultFilter={() => true}
        defaultInputValue={mobileAppConfiguration?.metadata?.name || searchTerm || ''}
        emptyNode={<EmptyState searchTermExists={!!searchTerm} />}
        isLoading={showLoadingState}
        items={sectionLayout}
        label="Select an app to test"
        listLayoutOptions={layoutOptions}
        menuTrigger="focus"
        onInputChange={handleSearch}
        onSelectionChange={(selectedFileId) => {
          shouldTriggerSearchRoutine.current = false; // set flag to false to prevent the next call to handleSearch being executed
          adaptToFileSelection(files.find((file) => file.id === selectedFileId) ?? null);
        }}
        placeholder="Search for an app"
        popoverFooter={moreFilesAvailable ? <LinkToAppStorage /> : null}
        selectedKey={mobileAppConfiguration?.id || null}
        startAdornment={<SelectedStateIcons />}
        textAdornment={<SelectedStateVersions />}
      >
        {/* Use a render func instead of array map. See https://react-spectrum.adobe.com/react-stately/collections.html#why-not-array-map for details. */}
        {(section: SectionLayout) => (
          <OptionGroup
            key={section.id}
            items={section.files}
            title={<span className="ml-3">{section.label}</span>}
          >
            {(file) => (
              <Option
                key={file.id}
                className={Styles['combobox-option']}
                data-qa={`app-search-and-select-file-id-${file.id}`}
                textValue={file.getDisplayName()}
              >
                <FileOption file={file} />
              </Option>
            )}
          </OptionGroup>
        )}
      </ComboBox>
    </div>
  );
};

export default AppSearchAndSelect;
